import { useFormContext, useWatch } from 'react-hook-form';
import { useState } from 'react';

import { useTotalDepositContext } from '../../contexts/TotalDepositContext';

import type { Form } from '../../queries/schema/carFinance.schema';
import { CURRENCIES } from '../../../../types';

type UseFinanceCalculatorArgs = {
  minimumDepositValue: string;
  minimumDepositPercentage: string;
};

const useFinanceCalculator = (args: UseFinanceCalculatorArgs) => {
  const { minimumDepositValue, minimumDepositPercentage } = args;
  const { control, setValue, getValues, setError, clearErrors } =
    useFormContext<Form>();

  const [isCashDepositModalOpen, setIsCashDepositModalOpen] = useState(false);
  const [isTradeInValueModalOpen, setIsTradeInValueModalOpen] = useState(false);
  const [isTotalDepositModalOpen, setIsTotalDepositModalOpen] = useState(false);
  const [isOutstandingFinanceModalOpen, setIsOutstandingFinanceModalOpen] =
    useState(false);

  const [
    cashDeposit,
    tradeInValue,
    outstandingFinance,
    totalDeposit,
    price,
    minimumFinanceAmount,
    pricePerMonth,
  ] = useWatch<
    Form,
    [
      'loanDetails.cashDeposit',
      'loanDetails.tradeInValue',
      'loanDetails.outstandingLoan',
      'loanDetails.totalDeposit',
      'carDetails.price',
      'loanDetails.minimumFinanceAmount',
      'loanDetails.pricePerMonth',
    ]
  >({
    control,
    name: [
      'loanDetails.cashDeposit',
      'loanDetails.tradeInValue',
      'loanDetails.outstandingLoan',
      'loanDetails.totalDeposit',
      'carDetails.price',
      'loanDetails.minimumFinanceAmount',
      'loanDetails.pricePerMonth',
    ],
  });

  const { isValidDeposit } = useTotalDepositContext();

  const formatPrice = (value: number) =>
    new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: CURRENCIES.EUR,
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(value);

  const formattedMinimumDepositValue = formatPrice(
    parseInt(minimumDepositValue),
  );

  const formattedMinimumFinanceAmount = formatPrice(minimumFinanceAmount);

  const onChangeHandlerFinanceInputs = (args: {
    cashDeposit: number | undefined;
    tradeInValue: number | undefined;
    outstandingFinance: number | undefined;
    fieldName:
      | 'loanDetails.cashDeposit'
      | 'loanDetails.outstandingLoan'
      | 'loanDetails.tradeInValue';
  }) => {
    const {
      cashDeposit = 0,
      tradeInValue = 0,
      outstandingFinance = 0,
      fieldName,
    } = args;

    const totalDeposit = cashDeposit + tradeInValue - outstandingFinance;
    const financeAmount = (price ?? 0) - totalDeposit;

    /**
     * We set these values of totalDeposit and financeAmount manually, rather than using derived state, for the values for these fields
     * to ensure that the correct values associated with the state of each of these fields are included in the submission.
     *
     * While the input will behave as expected, the state for both of these fields will not be updated and so will remain empty on submit.
     */
    setValue('loanDetails.totalDeposit', totalDeposit);
    setValue('loanDetails.financeAmount', Math.max(financeAmount ?? 0, 0));

    /**
     * We alert the user when the input changes rather than on submit to make it clear immediately that something is wrong,
     * before they proceeding any further in the application process.
     *
     * The finance calculator is unique in that there is no visual indication the panel is complete, unlike the other panels.
     */
    if (financeAmount < minimumFinanceAmount) {
      setError('loanDetails.financeAmount', {
        message: `Minimum finance amount is <strong>${formattedMinimumFinanceAmount}</strong>`,
      });
      setError(fieldName, {
        message: '',
      });
    } else {
      clearErrors([
        'loanDetails.totalDeposit',
        'loanDetails.cashDeposit',
        'loanDetails.financeAmount',
        'loanDetails.outstandingLoan',
        'loanDetails.tradeInValue',
      ]);
    }
  };

  const onBlurFinanceInputs =
    (
      fieldName:
        | 'loanDetails.cashDeposit'
        | 'loanDetails.outstandingLoan'
        | 'loanDetails.tradeInValue',
    ) =>
    () => {
      if (totalDeposit < Number(minimumDepositValue)) {
        setError('loanDetails.totalDeposit', {
          message: `Minimum deposit is <strong>${formattedMinimumDepositValue} (${minimumDepositPercentage}%)</strong>`,
        });
        setError(fieldName, {
          message: '',
        });
      }
    };

  /**
   * If price per month is 0, changing the amount of monthly repayments will not make a difference
   */
  const isMonthlyRepaymentsDisabled = !isValidDeposit || pricePerMonth === 0;

  return {
    price,
    control,
    getValues,
    formattedMinimumDepositValue,
    onChangeHandlerFinanceInputs,
    tradeInValue,
    outstandingFinance,
    cashDeposit,
    onBlurFinanceInputs,
    isCashDepositModalOpen,
    setIsCashDepositModalOpen,
    isTradeInValueModalOpen,
    setIsTradeInValueModalOpen,
    isTotalDepositModalOpen,
    setIsTotalDepositModalOpen,
    isOutstandingFinanceModalOpen,
    setIsOutstandingFinanceModalOpen,
    isMonthlyRepaymentsDisabled,
  };
};

export { useFinanceCalculator };
